import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Edit2, Trash2, Search, User, Shield, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

export const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'employee',
    isActive: true
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      role: 'employee',
      isActive: true
    });
    setEditDialog({ open: true, user: null });
  };

  const openEditDialog = (user) => {
    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone || '',
      role: user.role,
      isActive: user.isActive !== false
    });
    setEditDialog({ open: true, user });
  };

  const handleSave = async () => {
    try {
      if (editDialog.user) {
        const updateData = { ...formData };
        delete updateData.password;
        delete updateData.email;
        await api.patch(`/admin/users/${editDialog.user.id}`, updateData);
        toast.success('Utilisateur mis à jour');
      } else {
        await api.post('/admin/users', formData);
        toast.success('Utilisateur créé');
      }
      setEditDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      manager: 'bg-blue-100 text-blue-800 border-blue-200',
      employee: 'bg-green-100 text-green-800 border-green-200'
    };
    const icons = {
      admin: Shield,
      manager: ShieldCheck,
      employee: User
    };
    const labels = {
      admin: 'Admin',
      manager: 'Manager',
      employee: 'Employé'
    };
    const Icon = icons[role];
    return (
      <Badge className={`${styles[role]} border flex items-center gap-1 text-xs`}>
        <Icon size={12} />
        {labels[role]}
      </Badge>
    );
  };

  const filteredUsers = users.filter(user => 
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Mobile Responsive */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1810]">Gestion des Utilisateurs</h1>
            <p className="text-sm md:text-base text-[#5D4037] mt-1">Gérez les comptes et les permissions</p>
          </div>
          <Button onClick={openCreateDialog} className="bg-[#C9A96E] hover:bg-[#A67B5B] w-full sm:w-auto">
            <Plus className="mr-2" size={20} />
            Ajouter un utilisateur
          </Button>
        </div>
      </div>

      {/* Search - Mobile Optimized */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
            <Input
              placeholder="Rechercher par nom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Grid - Mobile Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {filteredUsers.map((user) => (
          <Card key={user.id} className={!user.isActive ? 'opacity-60' : ''}>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#C9A96E] flex items-center justify-center text-white font-bold text-base md:text-lg shrink-0">
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#2C1810] text-sm md:text-base truncate">{user.firstName} {user.lastName}</p>
                  <p className="text-xs md:text-sm text-[#5D4037] truncate">{user.email}</p>
                  {user.phone && <p className="text-xs md:text-sm text-[#A67B5B]">{user.phone}</p>}
                </div>
              </div>
              
              <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
                {getRoleBadge(user.role)}
                {!user.isActive && (
                  <Badge variant="outline" className="text-red-600 border-red-300 text-xs">Inactif</Badge>
                )}
              </div>

              {user.id !== currentUser?.id && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-[#E8DDD4]">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(user)}
                    className="flex-1 text-xs md:text-sm"
                  >
                    <Edit2 size={14} className="mr-1" /> Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setDeleteDialog({ open: true, user })}
                    className="text-red-600 hover:text-red-700 shrink-0"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <User size={48} className="mx-auto text-[#D4C4B5] mb-4" />
          <p className="text-[#5D4037]">Aucun utilisateur trouvé</p>
        </div>
      )}

      {/* Create/Edit Dialog - Mobile Optimized */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="w-[95vw] sm:w-full max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editDialog.user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!editDialog.user}
              />
            </div>

            {!editDialog.user && (
              <div className="space-y-2">
                <Label>Mot de passe</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employé</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label>Compte actif</Label>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, user: null })} className="w-full sm:w-auto">
                Annuler
              </Button>
              <Button onClick={handleSave} className="bg-[#C9A96E] hover:bg-[#A67B5B] w-full sm:w-auto">
                {editDialog.user ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation - Mobile Optimized */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="w-[95vw] sm:w-full max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le compte de {deleteDialog.user?.firstName} {deleteDialog.user?.lastName} sera supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteDialog.user?.id)}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
